import { StrictMode, Suspense, lazy } from 'react';
import { createRoot } from 'react-dom/client';
import { ApiProvider } from '@restaurant/api-client';

import { guestMenuToken } from './guest/menu-path';

import { startErrorReporting, startTheme } from '@restaurant/ui';
import '@restaurant/ui/tokens.css';

// After a deploy, the files an open page was built against are gone, and the
// next section it opens fails to download. Reload once to pick up the new
// build; the guard stops a loop when the failure is something else, which then
// shows on the page as any other error does.
window.addEventListener('vite:preloadError', (event) => {
  const key = 'restaurant.reloaded-for-deploy';
  try {
    if (Date.now() - Number(sessionStorage.getItem(key) ?? 0) < 10_000) return;
    sessionStorage.setItem(key, String(Date.now()));
  } catch {
    return;
  }
  event.preventDefault();
  window.location.reload();
});

const root = document.getElementById('root');
if (!root) throw new Error('#root is missing from index.html');

// A guest who scanned the QR code on their table: `/m/<token>`. Not the back
// office at all — no sign-in, none of its code downloaded, and nothing started
// that a guest has not agreed to (error reporting included). Held in the hand,
// so the hand's type sizes.
const guestToken = guestMenuToken(window.location.pathname, window.location.hostname);

if (guestToken !== null) {
  document.documentElement.dataset.room = 'hand';
  startTheme();

  const GuestMenuPage = lazy(() =>
    import('./guest/menu').then((m) => ({ default: m.GuestMenuPage })),
  );

  createRoot(root).render(
    <StrictMode>
      {/* Its own store, which nothing ever writes: the menu is read with no
          session, and a staff session on the same device is never touched. */}
      <ApiProvider baseUrl="" storageKey="restaurant.guest">
        <Suspense fallback={null}>
          <GuestMenuPage token={guestToken} />
        </Suspense>
      </ApiProvider>
    </StrictMode>,
  );
} else {
  // The back office: closest reading distance of the three, so the densest
  // type and the smallest targets. Still a tablet, never a desktop.
  document.documentElement.dataset.room = 'office';

  // First, so an error in the first render is reported too. A no-op unless
  // this deployment has a DSN (packages/ui/src/error-reporting.ts).
  startErrorReporting('admin');

  // Before the first render, so a dark device never flashes the light ground.
  startTheme();

  const supplierPortal = window.location.pathname.startsWith('/supplier');
  const Root = lazy(() =>
    import('./back-office').then((m) => ({
      default: supplierPortal ? m.SupplierRoot : m.BackOffice,
    })),
  );

  createRoot(root).render(
    <StrictMode>
      <Suspense fallback={null}>
        <Root />
      </Suspense>
    </StrictMode>,
  );
}
